﻿import {afterLoad, startLoadingAnimation, stopLoadingAnimation} from "@/Startup"
import {validateForm} from "@/Validation"
import {CookieConsentType, GtmEventType, Site} from "@/enums";
import {
    GtmAddToCartOptions,
    GtmFilterOptions,
    GtmOptions,
    CookieConsents,
    PromotionParams
} from "@/interfaces";
import {StatusCodes} from "http-status-codes"
import TagManager from 'react-gtm-module';
import {closeModal} from "@/sharedComponents/Modal";

type RequestMethod = "GET"|"POST"
export const cookieConsentsLoaded = "cookieConsentsLoaded";

function getHashCode(s: string): number {
    let hash = 0;
    let i: number, chr: number, len: number;
    if (s.length === 0) return hash;
    for (i = 0, len = s.length; i < len; i++) {
        chr = s.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

export function get(x: string): HTMLElement {
    return document.getElementById(x);
} // ID järgi küsimise alias
export function $(x: string): HTMLElement {
    return document.querySelector(x) as HTMLElement;
} // Selector, eeldab ühte tulemust
export function $$(x: string): NodeListOf<HTMLElement> {
    return document.querySelectorAll(x) as NodeListOf<HTMLElement>;
} // Selector, eeldab mitut tulemust

export const enum Key{
    ArrowUp = 'ArrowUp',
    ArrowDown = 'ArrowDown',
    ArrowLeft = 'ArrowLeft',
    ArrowRight = 'ArrowRight',
    Enter = 'Enter',
    Escape = 'Escape',
    Tab = 'Tab',
    Space = ' ',
    SpaceAlt = 'Spacebar'
}
export function hide(element: HTMLElement) {
    element?.classList.add("d-none");
}

export function show(element: HTMLElement) {
    element?.classList.remove("d-none");
}

export function toggle(element: HTMLElement, show?: boolean){
    const force = show == null ? show : !show;
    element?.classList.toggle("d-none", force);
}

export function documentHasVisibleFocus() {
    let hasVisibleFocus = true;
    let activeElement: any = document.activeElement;
    if (!activeElement || activeElement === document.body || activeElement === document) {
        hasVisibleFocus = false;
    }
    return hasVisibleFocus;
}

export function toggleElements(elementsToToggle: {element: string, show?: boolean}[]) {
    elementsToToggle?.forEach(({element, show}) => {
        $$(element)?.forEach(elem => toggle(elem, show))
    });
}

export function sleep(ms) {
    return new Promise(x => setTimeout(x, ms));
}

export function waitForElement(selector: string, timeout = 5000): Promise<Element | null> {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) {
            return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(() => {
            if (document.querySelector(selector)) {
                observer.disconnect();
                resolve(document.querySelector(selector));
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Add timeout to avoid waiting forever
        setTimeout(() => {
            observer.disconnect();
            resolve(null);
        }, timeout);
    });
}

export function clickOnEnterKey(event: KeyboardEvent, elem: HTMLElement, force = false) {
    if (force || event.key === Key.Enter) {
        event.preventDefault();
        elem.click();
    }
}

export function findClosestMatchingChildWithClass(target: HTMLElement, className: string) {
    for (const node of Array.from(target.childNodes)) {
        if (node instanceof HTMLElement && node.classList.contains(className)) {
            return node;
        }
    }
    return null;
}

export function isHidden(element: HTMLElement){
    return element.classList.contains("d-none")
}

export function isVisible(element: HTMLElement) {
    return element?.offsetParent !== null;
}
export function isVisibleInViewport(element: HTMLElement) {
    if (element?.offsetParent === null) return false;
    const rect = element.getBoundingClientRect();
    return rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
}

export function closestTabbable(element: HTMLElement) {
    if (!element) return null
    if ((element.tabIndex === 0 || ["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(element.tagName) && element.tabIndex !== -1) && isVisible(element)) {
        const htmlInput = element as HTMLInputElement
        if (htmlInput && htmlInput.type === "radio") {
            return htmlInput.checked ? closestTabbable(element.parentElement) : null
        }
        return element
    }
    return closestTabbable(element.parentElement)
}

/**
 * laeb etteantud JS faili ja peale seda execute'ib callbacki
 * @param src js faili path
 * @param callback callback mis täidetakse faili laadimisel (kui juba laetud, siis kohe, sünkroonselt)
 * @param ignoreConsent kui määratud, siis laetakse JS fail ka siis kui kasutaja pole cookieConsent'i andnud
 */
export function execWithContext(src: string, callback: Function, ignoreConsent = false) {
    if (!ignoreConsent && !getAndSetCookieConsents().performance) return;
    const id = `i${getHashCode(src).toString(36)}`;
    if (document.getElementById(id)) {
        callback();
        return;
    }
    const js = document.createElement("script");
    js.id = id;
    js.onload = () => callback();
    js.src = src;
    js.type = 'module';
    const fjs = document.getElementsByTagName("script")[0];
    fjs.parentNode.insertBefore(js, fjs);
}

/**
 * laeb etteantud CSS faili ja peale seda execute'ib callbacki
 * @param href CSS faili href
 * @param callback callback mis täidetakse faili laadimisel (kui juba laetud, siis kohe, sünkroonselt)
 * @param ignoreConsent kui määratud, siis laetakse CSS fail ka siis kui kasutaja pole cookieConsent'i andnud
 */
export function loadCss(href: string, callback: Function, ignoreConsent = false) {
    if (!ignoreConsent && !getAndSetCookieConsents().essential) return;
    const id = `i${getHashCode(href).toString(36)}`;

    if (document.getElementById(id)) {
        callback();
        return;
    }
    const lnk = document.createElement("link");
    lnk.id = id;
    lnk.onload = () => callback();
    lnk.rel = 'stylesheet';
    lnk.type = 'text/css';
    lnk.href = href;
    const fjs = document.getElementsByTagName("link")[0];
    fjs.parentNode.insertBefore(lnk, fjs);
}

export function remove(el: Element) {
    el.parentNode.removeChild(el);
}

/**
 * event.target peab olema element avatava/suletava elemendi sees
 */
export function toggleTargetClosestSelector(target: Element, closestSelector: string, toggleSelector: string, multiselect: boolean, maxCount?: number) {
    const toggleElement = target.closest(closestSelector);
    const isOpen = toggleElement.classList.contains(toggleSelector);

    if (isOpen) {
        toggleElement.classList.remove(toggleSelector);
        toggleElement.querySelectorAll("li").forEach(x => hide(x));
    } else {
        toggleElement.classList.add(toggleSelector);

        const items = toggleElement.querySelectorAll("ul>li").length;
        toggleElement.querySelectorAll("ul>li").forEach((x, i) => {
            toggle(x as HTMLElement, !maxCount || i < maxCount || items <= maxCount + 1)
        });
        if (toggleElement.querySelector("li:last-child>.show-more")) {
            show(toggleElement.querySelector("li:last-child"));
        }
    }

    if (multiselect) return;

    Array.from(toggleElement.parentElement.children).forEach(x => x === toggleElement ? null : (x as Element).classList.remove(toggleSelector));
}

export function getFileNameFromResponse(response: Response) {
    let filename = "";
    const disposition = response.headers.get("content-disposition");
    if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
        }
    }
    return filename;
}

/* Rejects promise on unsuccessful fetch and raises error with exception contents */
async function handleRedirectAndErrors(result: Response): Promise<Response> {
    if (result.status === StatusCodes.IM_A_TEAPOT) {
        window.location.href = await result.text();
        return result;
    }
    if (!result.ok) {
        if (result.headers.get("Content-Type") === "application/json") {
            const err = await result.json();
            // throw ajaxError, as these are the only ones we display
            if (!!err.ajax) {
                stopLoadingAnimation();
                get("message-container").innerHTML = err.message;
                throw 0;
            }
        }
        return result;
    }
    return result;
}

async function fetchLikeAjax(input: RequestInfo, init: RequestInit, dontPlayAjax: boolean = false): Promise<Response> {
    if (dontPlayAjax) {
        return fetch(input, init).then(handleRedirectAndErrors);
    }
    init = init || {};
    init.headers = init.headers || {};
    init.headers["X-Requested-With"] = "XMLHttpRequest";
    return fetch(input, init).then(handleRedirectAndErrors);
}

async function fetchDataInternal(url, data, method: RequestMethod = "POST", dontPlayAjax: boolean = false, withLoadingAnimation: boolean = true) {
    if (withLoadingAnimation) {
        startLoadingAnimation();
    }
    const request = {
        method: method, // *GET, POST, PUT, DELETE, etc.
        credentials: "same-origin", // include, *same-origin, omit
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    } as RequestInit;
    if (method !== "GET") {
        request.body = JSON.stringify(data); // body data type must match "Content-Type" header
        request.headers = {
            'Content-Type': "application/json"
            // 'Content-Type': 'application/x-www-form-urlencoded',
        }
    }
    return fetchLikeAjax(url, request, dontPlayAjax);
}

/**
 * Tagab, et afterload meetod kutsutakse välja pärast callbacki
 */
function processFetchResponse(promise: Promise<any>, callback?: Function, withLoadingAnimation: boolean = true) {
    if (callback) {
        promise.then(x => callback(x)).then(() => afterLoad(withLoadingAnimation));
    } else {
        promise.then(() => afterLoad(withLoadingAnimation));
    }
}

export function GetDataJsonSync(url, callback?: Function){
    const xhr = new XMLHttpRequest();
    xhr.overrideMimeType("application/json");
    xhr.open("GET", url, false);
    startLoadingAnimation();
    xhr.send(null);
    if(xhr.status === 200){
        stopLoadingAnimation();
        callback(JSON.parse(xhr.response));
    }
}

/**
 * POST-meetodiga päring URL-ile, annab callback'ile parameetriks JSON-objekti
 */
export async function postDataJson(url, data = {}, callback?: Function) {

    fetchDataInternal(url, data).then(response => processFetchResponse(response.json(), callback));
}

/**
 * POST-meetodiga päring URL-ile, annab callback'ile parameetriks HTML-vastuse
 */
export async function postDataText(url, data = {}, callback?: Function) {
    fetchDataInternal(url, data).then(response => processFetchResponse(response.text(), callback));
}

/**
 * POST-meetodiga päring URL-ile, annab callback'ile parameetriks Response-objekti
 */
export async function postData(url, data = {}, callback?: Function) {
    processFetchResponse(fetchDataInternal(url, data), callback);
}

/**
 * GET-meetodiga päring URL-ile, annab callback'ile parameetriks Response-objekti
 */
export async function getData(url, callback?: Function, withLoadingAnimation: boolean = true) {
    processFetchResponse(fetchDataInternal(url, {}, "GET", false, withLoadingAnimation), callback);
}

/**
 * GET-meetodiga päring URL-ile, annab callback'ile parameetriks JSON-objekti
 */
export async function getDataJson(url, callback?: Function, dontPlayAjax: boolean = false, withLoadingAnimation: boolean = true) {
    const response = await fetchDataInternal(url, {}, "GET", dontPlayAjax, withLoadingAnimation);
    processFetchResponse(response.json(), callback, withLoadingAnimation);
}

/**
 * GET-meetodiga päring URL-ile, annab callback'ile parameetriks HTML-vastuse
 */
export async function getDataText(url, callback?: Function) {
    fetchDataInternal(url, {}, "GET", null, null).then(response => processFetchResponse(response.text(), callback));
}

const defaultLangs = ["et", "lv", "lt"];

function postFormInternal(form: HTMLFormElement, formData: FormData, callback?: Function,formaction?: string) {
    startLoadingAnimation();
    const activeElement = document.activeElement as HTMLInputElement;
    const closestForm = activeElement?.closest("form");
    let action = formaction ??
        activeElement?.getAttribute("formaction") ??
        closestForm?.querySelector("input[type='submit']")?.getAttribute("formaction") ??
        closestForm?.getAttribute("formaction") ?? form.getAttribute("formaction");
    if (action) {
        const lang = $("html").getAttribute("lang");
        if (defaultLangs.indexOf(lang) === -1 && !action.startsWith(`/${lang}`)) {
            action = `/${lang}${action}`;
        }
    } else {
        action = form.action;
    }
    
    fetchLikeAjax(action, {
        method: "POST",
        body: formData
    }).then(response => processFetchResponse(Promise.resolve(response), callback));
}

export function postFormData(action: string, formData: FormData, callback: Function) {
    fetchLikeAjax(action, {
        method: "POST",
        body: formData,
    }).then(response => processFetchResponse(response.json(), callback));
}

export function postFormCustom(form: HTMLFormElement, seed: Map<string, string>, callback?: Function, skipValidation = false) {
    if (!skipValidation && !validateForm(form)) return false;
    startLoadingAnimation();
    const formData = new FormData(form);
    if (seed.size) {
        Array.from(seed.entries()).forEach(x => formData.append(x[0], x[1] ?? ""));
    }
    postFormInternal(form, formData, callback);
    return true;
}

/**
 * Kui callback-i sees kirjutatakse üle HTML-i, tuleb selle käivitamine edasi lükata.
 * Tagastades callback-i sisu return-iga, garanteerime, et afterload teeb oma bindimised
 * peale seda kui callback on ohjad tagasi tema kätte andnud.
 */
export function initForm(elemId: string, callback?: Function, validationFailedCallback?: Function) {
    const form = get(elemId) as HTMLFormElement;
    if (form) {
        form.addDataBoundEventListener("submit",
            (e: SubmitEvent) => {
                if (validateForm(form)) {
                    postFormInternal(form, new FormData(form), callback, e.submitter?.getAttribute("formaction"));
                }
                else if(validationFailedCallback){
                    validationFailedCallback();
                }
            });
    }
}

/**
 * Abifunktsioonid cookie'de halduseks
 * source: https://stackoverflow.com/a/24103596
 * Loob küpsise
 */
export function setCookie(name: string, value: string, days: number) {
    let expires = "";
    if (days) {
        let date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = `; expires=${date.toUTCString()}`;
    }
    let domain = window.config.cookieDomain
    if (window.location.hostname.includes("elesen.lt")){
        domain = "elesen.lt"
    }
    document.cookie = name + "=" + value + expires + "; path=/;domain=" + domain ;
}

/**
 * Kustutab küpsise
 */
export function customizeCookies(name: string) {
    setCookie(name, "", -1);
}

export function getAndSetCookieConsents(): CookieConsents {
    if (window.config.cookieConsents) return window.config.cookieConsents;
    const cookieString = getCookie("CookieConsent") || ""
    const marketing = cookieString.length > 0 && cookieString.includes(CookieConsentType.Marketing)
    const performance =  cookieString.length > 0 && cookieString.includes(CookieConsentType.Performance)
    window.config.cookieConsents = {essential: true, marketing: marketing, performance: performance} as CookieConsents
    return window.config.cookieConsents
}

/**
 * Tagastab küpsise nime järgi
 */
export function getCookie(name: string): string {
    let cookie: string = null;
    document.cookie.split(";").forEach(x => {
        const trimmed = x.trim();
        if (trimmed.startsWith(`${name}=`)) {
            cookie = decodeURIComponent(trimmed.substring(name.length + 1));
            return;
        }
    });
    return cookie;
}

export function consentToNoCookies() {
    const cookieModal = get("customize-cookies-modal")
    if(cookieModal){
        cookieModal.querySelectorAll("input[type=checkbox]").forEach((x: HTMLInputElement) => x.checked = false)
    }
    consentToSelectedCookies({essential: true, marketing: false, performance: false})
}
export function consentToAllCookies() {
    const cookieModal = get("customize-cookies-modal")
    if(cookieModal){
        cookieModal.querySelectorAll("input[type=checkbox]").forEach((x: HTMLInputElement) => x.checked = true)
    }
    consentToSelectedCookies({essential: true, marketing: true, performance: true})
}

export function consentToCookiePolicy() {
    const marketing = $("[data-cookie-marketing]") as HTMLInputElement;
    const performance = $("[data-cookie-performance]") as HTMLInputElement;
    const consents = {
        essential: true,
        marketing: marketing?.checked,
        performance: performance?.checked
    } as CookieConsents;
    consentToSelectedCookies(consents)
}

export const isStrictSite = window.config.site == Site.EuronicsLV || window.config.site == Site.ElesenLT

function consentToSelectedCookies(consents: CookieConsents) {
    
    const consentsCookie = "E"+(consents.marketing ? "M" : "")+(consents.performance ? "P" : "")
    const existingConsents = getAndSetCookieConsents();
    const consentsChanged = existingConsents.marketing != consents.marketing || existingConsents.performance != consents.performance;
    const cookieConsent = getCookie("CookieConsent")
    
    // If consents are or cookies are not yet set, then set new values to cookies and reload the page. Otherwise, just close the modal.
    if (consentsChanged || cookieConsent == null) {        
        if (isStrictSite) {
                            
            const cookies = document.cookie.split("; ")
            cookies.forEach(c => {
                const cookieName = c.split("=")[0]
                const cookie = encodeURIComponent(cookieName) + `=; expires=Thu, 01-Jan-1970 00:00:01 GMT;path=/;domain=${window.config.cookieDomain}`
                if (cookieName === "CookieConsent") {
                    return
                }
                setCookie(cookieName, cookie, -1)
            })
        }
        setCookie("CookieConsent", consentsCookie, 365)
        window.location.reload()
    }

    get("cookieConsentBlock")?.remove()
    const comparisonMessage = get("comparison-message-container");
    if(comparisonMessage)
        comparisonMessage.style.top = "0";

    closeModal()
    get("cookie-declaration-modal")?.remove() // Needed only once
    alignSidePanelToHeaderTopPosition();
}

/**
 * If a message is visible, then realign opened side panel to header top position
 * We have to take into account if it is visible in the viewport
 */
export function alignSidePanelToHeaderTopPosition() {
    const header = $(".header");
    const message = $('#message-container');

    let observer = new IntersectionObserver(entries => {
        entries.forEach((e) => {
            if(!e.isIntersecting) {
                $(".lr-panel--active")?.style.removeProperty("top");
                $(".panel__overlay--active")?.style.removeProperty("top");
            } else {
                $(".lr-panel--active")?.style.setProperty("top", header?.offsetTop + "px");
                $(".panel__overlay--active")?.style.setProperty("top", header?.offsetTop + "px");
            }
        });
    }, {
        rootMargin: "0px",
        threshold: 1.0,
    });
    observer.observe(message);
}

/**
 * Ümbritseb sisendist leitud otsingufraasi <mark>...</mark> tagidega
 * @param input - sisendstring
 * @param searchString - otsingufraas
 */
export function highlight(input: string, searchString: string) {
    if (!input?.length || !searchString?.length) return input

    let startIndex = 0
    let index = 0
    const inputLo = input.toLowerCase()
    const searchStringLo = searchString.toLowerCase()
    const searchStringLen = searchString.length

    let output = ""
    while ((index = inputLo.indexOf(searchStringLo, startIndex)) > -1) {
        const replacement = input.substring(startIndex, index) + "<mark>" + input.substring(index, searchString.length) + "</mark>"
        output += replacement
        startIndex = index + searchStringLen;
    }
    return output + input.substring(startIndex);
}

export function parseAndCallGtmPurchaseEvent(element: HTMLElement) {
    const event = element.getAttribute("data-gtm-event") as GtmEventType
    if (event === GtmEventType.Purchase) {
        // Ostu sooritamisel (summary leht) saadame lisaks kõigi makseviiside kohta teavet
        const payments: string[] = element.getAttribute("data-gtm-payments").split(",")
        payments.forEach(action => {
            gtmSimpleEvent(GtmEventType.PaymentMethod, action)
        })
        const data = JSON.parse(document.getElementById('purchase').textContent);
        TagManager.dataLayer({
            dataLayer: {
                event: GtmEventType.Purchase.toString(),
                ...data.params
            }
        });
    }
}

export function gtmSimpleEvent(eventType: GtmEventType, action?: string, callback?: Function) {
    gtmEvent(eventType, action ?? "", "", callback, null, null);
}

export function gtmEvent(eventType: GtmEventType, action: string, label: string, callback: Function, 
                         filters?: GtmFilterOptions, addToCart?: GtmAddToCartOptions) {
    if (eventType === GtmEventType.GtmInitConsent) {
        TagManager.dataLayer({
            dataLayer: {
                event: GtmEventType.GtmInitConsent
            }
        })
        return
    }
    let data: GtmOptions = {
        event: eventType,
        eventCategory: eventType,
        eventAction: action,
    };
    if (callback) {
        // kui GTM'i pole siiani laetud, siis ignoreerime seda ja kävitame otse callbacki
        if (window.google_tag_manager === undefined) {
            callback();
            return;
        }
        data.eventCallback = callback
        data.eventTimeout = 500
    }
    if (label) {
        data.eventLabel = label;
    }
    data = {...data, ...filters}
    data = {...data, ...addToCart}
    if (!!addToCart) {
        TagManager.dataLayer({
            dataLayer: {
                event: addToCart.eventName,
                value: addToCart.value,
                items: addToCart.itemsGa4,
                metaEventId: addToCart.metaEventId
            }
        });
    }
    else{
        TagManager.dataLayer({
            dataLayer: data
        });
    }
}

/*
 * addEventListeneri wrapper, mis kontrollib data-bound atribuudi olemasolu (kui olemas, siis ei bindi callbacki).
 * Kui customEventName on määratud, siis kontrollib data-customEventName-bound atribuudi olemasolu.
 */

Element.prototype.addDataBoundEventListener = function (this: Element, eventName: string, callback: EventListener, customEventName: string = null) {
    const attr = customEventName ? `data-${customEventName}-bound` : "data-bound";
    if (this.hasAttribute(attr)) return;

    this.setAttribute(attr, "");
    this.addEventListener(eventName, callback);
}

export function updateLanguageUrls(element: HTMLElement) {
    const hasAltLangUrls = element.hasAttribute("data-url-eng");
    if (!hasAltLangUrls) return;
    $$(".language-selector [data-lang]").forEach(x => {
        const lang = x.getAttribute("data-lang");
        const url = element.getAttribute(`data-url-${lang}`);
        if (!url) return;
        x.setAttribute("href", url);
    });
}

export function downloadFileFromBlob(blob: Blob, fileName: string) {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    link.remove();
}

/**
 * Scrollib aktiivse responsive tabi ekraanil nähtavale
 * */
export function centerActiveTab() {

    if (!window.isResponsive) return;

    const activeTab = $(".responsive-sort .custom-radio.checked, .responsive-sort .tabs-menu__item--active");
    if (!activeTab) return;

    $(".responsive-sort")?.scrollTo({
        left: activeTab.offsetLeft - (window.innerWidth - activeTab.offsetWidth) * 0.5,
        behavior: 'smooth'
    });
}
export function sendPromotionGtmEvent(target: HTMLElement, ev: GtmEventType, force = false) {
    if (!target || !ev) return;
    let campaignId = parseInt(target.getAttribute("data-campaign-id"));
    let campaignName = null;
    if (campaignId == 0) {
        campaignId = null;
    }
    else{
        campaignName = target.getAttribute("data-campaign-name");
    }
    const creativeSlot = target.getAttribute("data-type");
    const creativeName = target.getAttribute("alt");
    const sent = target.getAttribute("data-sent");

    const data: PromotionParams = {
        creativeName,
        creativeSlot,
        promotionId: campaignId,
        promotionName: campaignName,
    }
    if (!sent || force) {
        TagManager.dataLayer({
            dataLayer: {
                event: ev,
                ...data
            }
        });
        target.setAttribute("data-sent", "1")
    }
}
export function preserveUrlParameters () {
    if (window.location.search.length == 0)
        return;
    const domainToDecorate = window.config.cookieDomain,

    queryParams = [
        "gclid",
        "gad_source",
        "gbraid",
        "fbclid",
        "_gl"
    ];
    // Check if any of the query params exist in the URL
    const hasAnyTrackingParam = queryParams.some(param => {
        return new RegExp("[?&]" + encodeURIComponent(param) + "=([^&]*)").test(window.location.search);
    });

    // Return early if none of the params are present
    if (!hasAnyTrackingParam) {
        return;
    }

    const links = document.querySelectorAll("a");

    // check if links contain domain from the domainToDecorate array and then decorates
    for (let linkIndex = 0; linkIndex < links.length; linkIndex++) {
        const link = links[linkIndex];
        if (link.href.indexOf(domainToDecorate) > -1 && link.href.indexOf("#") === -1) {
            link.href = decorateUrl(link.href);
        }
    }
    const buttons = document.querySelectorAll("button[data-url], input[data-url]");
    // check if links contain domain from the domainToDecorate array and then decorates
    for (let buttonIndex = 0; buttonIndex < buttons.length; buttonIndex++) {
        const button = buttons[buttonIndex];
        if (button instanceof HTMLElement &&
            button.dataset.url.indexOf(domainToDecorate) > -1 &&
            button.dataset.url.indexOf("#") === -1
        ) {
            button.setAttribute('data-url', decorateUrl(button.dataset.url));
        }
    }
    // In mobile we have tab-urls
    const tabButtons = document.querySelectorAll("button[data-tab-url]");
    for (let tabButtonIndex = 0; tabButtonIndex < tabButtons.length; tabButtonIndex++) {
        const tabButton = tabButtons[tabButtonIndex];
        if (tabButton instanceof HTMLElement &&
            tabButton.dataset.tabUrl.indexOf(domainToDecorate) > -1 &&
            tabButton.dataset.tabUrl.indexOf("#") === -1
        ) {
            tabButton.setAttribute('data-tab-url', decorateUrl(tabButton.dataset.tabUrl));
        }
    }

    // decorates the URL with query params
    function decorateUrl(urlToDecorate) {
        let initialUrl = urlToDecorate;
        let anyParam = false;
        urlToDecorate = (urlToDecorate.indexOf("?") === -1) ? urlToDecorate + "?" : urlToDecorate + "&";
        const collectedQueryParams = [];
        for (let queryIndex = 0; queryIndex < queryParams.length; queryIndex++) {
            if (getQueryParam(queryParams[queryIndex])) {
                anyParam = true
                collectedQueryParams.push(queryParams[queryIndex] + "=" + getQueryParam(queryParams[queryIndex]))
            }
        }
        if (!anyParam){
            return initialUrl
        }
        return urlToDecorate + collectedQueryParams.join("&");
    }
// borrowed from https://stackoverflow.com/questions/831030/
// a function that retrieves the value of a query parameter
    function getQueryParam(name) {
        if (name == (new RegExp("[?&]" + encodeURIComponent(name) + "=([^&]*)")).exec(window.location.search))
            return decodeURIComponent(name[1]);
    }
}